import type { ActivationStatus, ExplorationVolume } from '../types/exploration';

/** Shared EC2 group-name labels — one instance vs. a bucket of many (bubble chip, treemap/coverage wrappers, infrastructure filter). */
export const ec2InstanceLabel = 'EC2 Instance';
export const ec2InstancesLabel = 'EC2 Instances';

export const ACTIVATION_LABELS: Record<ActivationStatus, string> = {
  active: 'Autoscaling',
  toBeActivated: 'To be activated',
  potentiallySupported: 'Pending setup',
  unsupported: 'Unsupported',
};

export const ACTIVATION_COLORS: Record<ActivationStatus, string> = {
  active: 'var(--activation-active)',
  toBeActivated: 'var(--activation-toBeActivated)',
  potentiallySupported: 'var(--activation-potentiallySupported)',
  unsupported: 'var(--activation-unsupported)',
};

export function activationLabel(key: string): string {
  return ACTIVATION_LABELS[key as ActivationStatus] ?? key;
}

/** Title-cases an unmapped reason key as a readable fallback, e.g. 'needs_reboot' -> 'Needs Reboot'. */
export function formatReasonLabel(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function activationColor(key: string): string {
  return ACTIVATION_COLORS[key as ActivationStatus] ?? 'var(--color-gray-400)';
}

export const ACTIVATION_ORDER: ActivationStatus[] = [
  'active',
  'toBeActivated',
  'potentiallySupported',
  'unsupported',
];

export const ACTIVE_STATUSES = new Set([
  'managed',
  'datafied',
  'migrating',
  'unmigrating',
  'shrinking',
  'growing',
]);

export function isVolumeManaged(v: Pick<ExplorationVolume, 'status'> | null | undefined): boolean {
  return !!(v && ACTIVE_STATUSES.has(v.status));
}

export const REASON_PRIORITY = [
  'unsupported_os',
  'boot_volume',
  'host_platform_not_supported',
  'unsupported_volume_type',
  'unsupported_volume_size',
  'unsupported_fs',
  'unsupported_hypervisor',
  'unsupported_instance_type',
  'unsupported_virtualization_type',
  'encryption_state',
  'instance_not_running',
  'unattached',
  'unmounted',
  'bad_number_of_partitions',
  'lvm',
  'br_local_volume',
  'datafy_disabled',
];

export const REASON_LABELS: Record<string, string> = {
  unsupported_os: 'Unsupported OS',
  boot_volume: 'Boot Volume',
  host_platform_not_supported: 'Host Platform',
  unsupported_volume_type: 'Volume Type',
  unsupported_volume_size: 'Volume Size',
  unsupported_fs: 'Unsupported FS',
  unsupported_hypervisor: 'Hypervisor',
  unsupported_instance_type: 'Instance Type',
  unsupported_virtualization_type: 'Virtualization',
  encryption_state: 'Encryption',
  instance_not_running: 'Instance Stopped',
  unattached: 'Unattached',
  unmounted: 'Unmounted',
  bad_number_of_partitions: 'Partitions',
  lvm: 'LVM',
  br_local_volume: 'BR Local Volume',
  datafy_disabled: 'Datafy Disabled',
};

/**
 * Resolves a supportStatus key to its display label: 'Autoscaling Potential' for the
 * 'supported' bucket. 'unattached' is the one other hardcoded exception — unlike other
 * reasons, it's a structural sentinel several call sites key off directly
 * (buildBreadcrumbDisplay.ts, SUPPORT_TOP_LEVEL_EXCLUDED in buildCheckboxFilterOptions.ts),
 * so it can't be renamed by the backend the way a plain reason can, but it still needs a
 * human-facing label. Any other backend reason text passes through verbatim (the backend
 * now sends already-named reasons) — EXCEPT a raw snake_case identifier (still sent by some
 * older reason codes), which gets a REASON_LABELS lookup or a generic Title Case fallback so
 * it never reaches the UI as `unsupported_os`.
 */
export function resolveSupportLabel(key: string): string {
  if (key === 'supported') return 'Autoscaling Potential';
  if (key === 'unattached') return 'Unattached';
  // An underscore is the signal that this is a raw identifier rather than the backend's
  // already-formatted prose (no known reason code is otherwise ambiguous with free text).
  if (!key.includes('_')) return key;
  return REASON_LABELS[key] ?? formatReasonLabel(key);
}

const REASON_CATEGORY_COLORS: Record<string, string> = {
  node: 'var(--color-vivd-purple, #a515ff)',
  volume: 'var(--color-carrot-orange, #ef9641)',
  state: 'var(--color-gray-600, #828b98)',
  supported: 'var(--exploration-supported)',
  unknown: 'var(--color-gray-400, #ccced3)',
};

export function colorForSupport(key: string): string {
  if (key === 'supported') return REASON_CATEGORY_COLORS.supported;
  if (key === 'unknown') return REASON_CATEGORY_COLORS.unknown;
  return REASON_CATEGORY_COLORS.volume;
}

export function classifyVolume(
  v: Pick<
    ExplorationVolume,
    'status' | 'isDatafiable' | 'isPotentiallyDatafiable' | 'datafiableReasons' | 'datafiable'
  >
): Pick<
  ExplorationVolume,
  'activationStatus' | 'activationLabel' | 'supportStatus' | 'supportLabel'
> {
  let activation: ActivationStatus;
  if (ACTIVE_STATUSES.has(v.status)) activation = 'active';
  else if (v.isDatafiable === true) activation = 'toBeActivated';
  else if (v.isPotentiallyDatafiable === true) activation = 'potentiallySupported';
  else activation = 'unsupported';

  let supportKey: string;
  if (v.datafiable || v.isDatafiable) {
    supportKey = 'supported';
  } else {
    // The backend now sends a single, already-ranked, already-named reason
    // (`notDatafiableReason`) — use it as-is instead of re-ranking/re-naming a list on
    // the frontend (dropped the old 'no_autoscaler'/'agent_not_autoscaler' state-flag
    // filter too: it existed to skip those flags when picking the best of *several*
    // candidate reasons, which no longer applies to a single backend-provided value —
    // filtering it out would leave nothing and collapse a real reason down to 'Unknown').
    const reasons = Array.isArray(v.datafiableReasons) ? v.datafiableReasons : [];
    supportKey = reasons.length === 0 ? 'unknown' : reasons[0];
  }

  return {
    activationStatus: activation,
    activationLabel: ACTIVATION_LABELS[activation],
    supportStatus: supportKey,
    supportLabel: supportKey === 'unknown' ? 'Unknown' : resolveSupportLabel(supportKey),
  };
}

type ClassifiableVolume = Pick<
  ExplorationVolume,
  'status' | 'isDatafiable' | 'isPotentiallyDatafiable' | 'datafiableReasons' | 'datafiable'
>;

export function attachClassification(
  v: Omit<
    ExplorationVolume,
    'activationStatus' | 'activationLabel' | 'supportStatus' | 'supportLabel'
  > &
    ClassifiableVolume
): ExplorationVolume {
  return { ...v, ...classifyVolume(v) };
}
